import { FC, useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import { useLocation, useNavigate } from 'react-router-dom';

import Routes from '../../core/Routes';
import Page from '../../components/Page/Page';
import NewsTable from './NewsTable/NewsTable';
import Loader from '../../components/Loader/Loader';
import RoutesEnum from '../../core/enums/RoutesEnum';
import { BreakpointsEnum, useMediaQuery } from '../../core/hooks/useMediaQuery';
import useFeaturedNewsArticles from '../../core/apiHooks/useFeaturedNewsArticles';
import FeaturedArticlesSlider from './FeaturedArticlesSlider/FeaturedArticlesSlider';

import './News.styles.scss';
import cn from 'classnames';

export interface iNewsNavigationState {
    returnUrl?: string;
}

const News: FC = () => {
    const { isFeaturedNewsArticlesLoading, featuredNewsArticles } = useFeaturedNewsArticles();

    const location = useLocation();
    const navigate = useNavigate();
    const route = Routes.find(({ path }) => path === location.pathname) || Routes[0];
    const isMobile = useMediaQuery(BreakpointsEnum.SM);

    const navigateToArticle = useCallback(
        (articleId?: number) => {
            if (!articleId) return;

            const returnUrl = location.pathname + location.search;
            navigate(RoutesEnum.NewsArticle.replace(':id', String(articleId)), { state: { returnUrl } });
        },
        [navigate, location]
    );

    const featuredNewsArticlesMarkup = useMemo(() => {
        if (!featuredNewsArticles?.length) return [];

        return featuredNewsArticles.map((article, index) => {
            const isMainArticle = index === 0;
            return (
                <div
                    key={article.id}
                    className={cn('NewsCard', { 'NewsCard--main': isMainArticle })}
                    onClick={() => navigateToArticle(article.id)}
                >
                    <img className="NewsCard__image" src={article.featuredImage || ''} alt={article.title || ''} />
                    <div className="NewsCard__overlay">
                        <span className={cn('NewsCard__title', { 'NewsCard__title--side': !isMainArticle })}>
                            {article.title}
                        </span>
                        {isMainArticle && (
                            <>
                                <div className="NewsCard__date">
                                    {article.publishedDate ? dayjs(article.publishedDate).format('MMMM D, YYYY') : ''}
                                </div>
                                <span className="NewsCard__description">{article?.description || ''}</span>
                            </>
                        )}
                    </div>
                </div>
            );
        });
    }, [featuredNewsArticles, navigateToArticle]);

    return (
        <Page title={route.title} isShownStickyFooter>
            <div className="News">
                <div className="News__title">News</div>
                {isFeaturedNewsArticlesLoading ? (
                    <Loader />
                ) : (
                    <>
                        {isMobile ? (
                            <FeaturedArticlesSlider articles={featuredNewsArticles || []} navigateToArticle={navigateToArticle} />
                        ) : (
                            <div className="News__featured">
                                <div className="News__featured-bleed">
                                    {featuredNewsArticlesMarkup[0]}
                                    {featuredNewsArticlesMarkup.length > 1 && (
                                        <div className="News__side">{featuredNewsArticlesMarkup.slice(1)}</div>
                                    )}
                                </div>
                            </div>
                        )}
                        <div className="News__section">
                            <NewsTable />
                        </div>
                    </>
                )}
            </div>
        </Page>
    );
};

export default News;
